import { el } from '../utils/dom.js';

/**
 * Engine export snippets — shows code for loading the current map
 * in Godot, Phaser 3, or Unity.
 */
export class EngineExport {
  constructor(state) {
    this._state = state;
  }

  show() {
    const overlay = el('div', { className: 'dialog-overlay', onClick: (e) => {
      if (e.target === overlay) overlay.remove();
    }});
    const dialog = el('div', { className: 'dialog engine-export-dialog' });

    dialog.appendChild(el('h2', { className: 'dialog-title' }, 'Export for Engine'));

    const fileName = this._state.get('fileName') || 'untitled.tmj';
    const baseName = fileName.replace(/\.(tmj|json)$/i, '');
    const tilesets = this._state.get('tilesets') || [];
    const tilesetName = tilesets.length > 0 ? tilesets[0].name : 'tileset';
    const tilesetImage = tilesets.length > 0 ? tilesets[0].image : 'tileset.png';

    // Tab buttons
    const engines = [
      { id: 'godot', label: 'Godot', lang: 'gdscript' },
      { id: 'phaser', label: 'Phaser 3', lang: 'javascript' },
      { id: 'unity', label: 'Unity', lang: 'csharp' },
    ];

    const tabBar = el('div', { className: 'engine-tab-bar' });
    const codeArea = el('div', { className: 'engine-code-area' });
    let activeTab = 'godot';

    const snippets = {
      godot: this._godotSnippet(baseName, fileName, tilesetImage),
      phaser: this._phaserSnippet(baseName, fileName, tilesetName, tilesetImage),
      unity: this._unitySnippet(baseName, fileName, tilesetImage),
    };

    const renderCode = () => {
      codeArea.innerHTML = '';
      const pre = el('pre', { className: 'engine-code' });
      const code = el('code', {}, snippets[activeTab]);
      pre.appendChild(code);
      codeArea.appendChild(pre);

      // Copy button
      const copyBtn = el('button', { className: 'btn btn-sm engine-copy-btn', onClick: () => {
        navigator.clipboard.writeText(snippets[activeTab]).then(() => {
          copyBtn.textContent = 'Copied!';
          setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1500);
        });
      }}, 'Copy');
      codeArea.appendChild(copyBtn);

      // Update tab active state
      tabBar.querySelectorAll('.engine-tab').forEach(t => {
        t.classList.toggle('active', t.dataset.engine === activeTab);
      });
    };

    for (const engine of engines) {
      const tab = el('button', {
        className: `engine-tab${engine.id === activeTab ? ' active' : ''}`,
        'data-engine': engine.id,
        onClick: () => { activeTab = engine.id; renderCode(); },
      }, engine.label);
      tabBar.appendChild(tab);
    }

    dialog.appendChild(tabBar);
    dialog.appendChild(codeArea);
    renderCode();

    // Info text
    dialog.appendChild(el('div', {
      style: { fontSize: '11px', color: 'var(--text-dim)', marginTop: '8px' },
    }, 'Save your map as TMJ first, then use the snippet above in your game project.'));

    // Close button
    const btnRow = el('div', { className: 'dialog-buttons' });
    btnRow.appendChild(el('button', { className: 'btn', onClick: () => overlay.remove() }, 'Close'));
    dialog.appendChild(btnRow);

    overlay.appendChild(dialog);
    document.body.appendChild(overlay);
  }

  _godotSnippet(baseName, fileName, tilesetImage) {
    return `# Godot 4 — Load Tiled TMJ map
# 1. Install the "Tiled Map Importer" addon from AssetLib
# 2. Place ${fileName} and ${tilesetImage} in res://maps/

extends Node2D

@onready var tilemap: TileMap = $TileMap

func _ready():
    # Option A: Use Tiled importer addon (recommended)
    # Just drag ${fileName} into the scene — the addon handles it.

    # Option B: Manual loading via JSON
    var file = FileAccess.open("res://maps/${fileName}", FileAccess.READ)
    var json = JSON.parse_string(file.get_as_text())
    file.close()

    # Parse layers
    for layer in json["layers"]:
        if layer["type"] == "tilelayer":
            print("Layer: ", layer["name"], " (", layer["width"], "x", layer["height"], ")")
            # Set tiles from layer data array
            var idx = 0
            for y in range(layer["height"]):
                for x in range(layer["width"]):
                    var gid = layer["data"][idx]
                    if gid > 0:
                        tilemap.set_cell(0, Vector2i(x, y), 0, Vector2i(0, 0), gid)
                    idx += 1`;
  }

  _phaserSnippet(baseName, fileName, tilesetName, tilesetImage) {
    return `// Phaser 3 — Load Tiled TMJ map

class GameScene extends Phaser.Scene {
  constructor() {
    super('Game');
  }

  preload() {
    // Load map JSON and tileset image
    this.load.tilemapTiledJSON('${baseName}', 'assets/maps/${fileName}');
    this.load.image('${tilesetName}', 'assets/tilesets/${tilesetImage}');
  }

  create() {
    // Create tilemap from loaded JSON
    const map = this.make.tilemap({ key: '${baseName}' });

    // Add tileset image to the map
    const tileset = map.addTilesetImage('${tilesetName}', '${tilesetName}');

    // Create layers (use the layer names from your map)
    const groundLayer = map.createLayer('Ground', tileset, 0, 0);
    const objectsLayer = map.createLayer('Objects', tileset, 0, 0);
    const overheadLayer = map.createLayer('Overhead', tileset, 0, 0);

    // Set collision on specific tiles or layers
    // groundLayer.setCollisionByProperty({ collision: true });

    // Camera setup
    this.cameras.main.setBounds(0, 0, map.widthInPixels, map.heightInPixels);
  }
}

const config = {
  type: Phaser.AUTO,
  width: 800,
  height: 600,
  scene: GameScene,
};

const game = new Phaser.Game(config);`;
  }

  _unitySnippet(baseName, fileName, tilesetImage) {
    return `// Unity — Load Tiled TMJ map
// Option A: SuperTiled2Unity (recommended)
// 1. Install SuperTiled2Unity from Unity Package Manager
//    or download from https://seanba.itch.io/supertiled2unity
// 2. Drag ${fileName} into Assets/Tiled/
// 3. SuperTiled2Unity auto-imports as a prefab

// Option B: Manual Tilemap setup
using UnityEngine;
using UnityEngine.Tilemaps;
using System.IO;

public class MapLoader : MonoBehaviour
{
    public Tilemap groundTilemap;
    public Tilemap objectsTilemap;
    public TileBase[] tileAssets; // Assign in inspector

    void Start()
    {
        string json = File.ReadAllText(
            Path.Combine(Application.streamingAssetsPath, "maps/${fileName}")
        );
        var map = JsonUtility.FromJson<TiledMap>(json);

        foreach (var layer in map.layers)
        {
            if (layer.type != "tilelayer") continue;

            Tilemap target = layer.name == "Ground"
                ? groundTilemap
                : objectsTilemap;

            for (int y = 0; y < layer.height; y++)
            {
                for (int x = 0; x < layer.width; x++)
                {
                    int gid = layer.data[y * layer.width + x];
                    if (gid > 0 && gid <= tileAssets.Length)
                    {
                        // Unity Y is inverted from Tiled
                        var pos = new Vector3Int(x, layer.height - 1 - y, 0);
                        target.SetTile(pos, tileAssets[gid - 1]);
                    }
                }
            }
        }
    }
}

// Note: For production use, SuperTiled2Unity handles all of this
// automatically with proper tile slicing and layer mapping.`;
  }
}
